/**
 * Purpose:
 *   Renders the list of menu items in a navigation structure.
 *   Used by both desktop and mobile menu views.
 *
 * Notes:
 *   - Accepts menu state and handlers from parent Menu component
 *   - Supports collapsed mode for desktop view
 *   - Handles link clicks for mobile menu closure
 */

'use client';

import React from 'react';
import { MenuItem } from './MenuItem';
import { MENU_NAV } from './Menu.styles';
import type { MenuItemConfig } from '@/types/menu';

export interface MenuItemsProps {
  /** Array of menu items to render */
  items: MenuItemConfig[];
  /** Accessible label for the navigation block */
  ariaLabel: string;
  /** Set of expanded item IDs */
  expandedItems: Set<string>;
  /** Function to check if an item is active */
  isActive: (item: MenuItemConfig) => boolean;
  /** Function to check if a parent item is active */
  isParentActive: (item: MenuItemConfig) => boolean;
  /** Function to toggle expanded state of an item */
  onToggle: (itemId: string) => void;
  /** Function to align state when a menu link is clicked */
  onNavigate: (href: string) => void;
  /** Whether menu is in collapsed mode (desktop) */
  isCollapsed?: boolean;
  /** Callback when a link is clicked (for mobile menu closure) */
  onLinkClick?: () => void;
}

export const MenuItems: React.FC<MenuItemsProps> = ({
  items,
  ariaLabel,
  expandedItems,
  isActive,
  isParentActive,
  onToggle,
  onNavigate,
  isCollapsed = false,
  onLinkClick,
}) => {
  return (
    <nav className={MENU_NAV} aria-label={ariaLabel}>
      {items.map((item) => (
        <MenuItem
          key={item.id}
          item={item}
          isExpanded={expandedItems.has(item.id)}
          isActive={isActive(item)}
          isParentActive={isParentActive(item)}
          onToggle={() => onToggle(item.id)}
          onNavigate={onNavigate}
          isChildActive={isActive}
          onLinkClick={onLinkClick}
          isCollapsed={isCollapsed}
        />
      ))}
    </nav>
  );
};
